import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
} from '@mui/material';
import { GLYPHS, AppDialog } from '@/components/ui';
import type { Employee, MonthlyPayrollData, WorkSchedule } from '../../types/employee';
import { resolvePayType } from '../../types/employee';
import { useEmployeeData, useWorkScheduleData, useEmployeePayroll } from '../../hooks';
import { useAccountBookStore } from '../../store/accountBookStore';
import { payTypeChipLabel } from '../../lib/payroll/display-helper';
import { calculateTotalWorkHours } from '../../utils/payrollCalculations';
import { downloadElementAsPdf } from '../../services/pdfCaptureService';

/**
 * 인사기록 탭 — 법정 인사 서류 4종을 보유 데이터로 즉시 PDF 생성.
 *   근로자명부 · 출근부 · 임금대장 · 임금명세서 (근로기준법상 3년 보존 서류)
 * PDF는 기존 downloadElementAsPdf(화면 캡처 → A4) 패턴 재사용 — 한글 폰트 문제 없음.
 */

// A4 캡처 기준 픽셀 (downloadElementAsPdf 가 폭에 맞춰 페이지 분할)
const A4_PORTRAIT_WIDTH = 794;   // 210mm @96dpi
const A4_PORTRAIT_HEIGHT = 1123; // 297mm 환산 — 명세서 1인 1페이지 정렬용
const A4_LANDSCAPE_WIDTH = 1123;

type DocType = 'roster' | 'attendance' | 'ledger' | 'paystub' | 'dismissal' | 'severance';

interface PrintJob {
  type: DocType;
  /** 임금명세서: 'ALL' 또는 직원 id */
  employeeId?: string;
  fileName: string;
}

/** 해고 예고 통지서 입력값 */
interface DismissalForm {
  employeeId: string;
  dismissDate: string; // YYYY-MM-DD
  reason: string;
}

/** 퇴직금 산정서 입력값 */
interface SeveranceForm {
  employeeId: string;
  retireDate: string; // YYYY-MM-DD
  threeMonthPay: string; // 퇴직 전 3개월 임금총액 (입력 문자열)
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** 퇴직금 법정 산식 — 1일 평균임금 × 30일 × (재직일수 / 365) */
function calcSeverance(hireDate: Date, retireDate: Date, threeMonthPay: number) {
  const periodStart = new Date(retireDate);
  periodStart.setMonth(periodStart.getMonth() - 3);
  const periodDays = Math.max(1, Math.round((retireDate.getTime() - periodStart.getTime()) / DAY_MS));
  const avgDailyWage = threeMonthPay / periodDays;
  const serviceDays = Math.floor((retireDate.getTime() - hireDate.getTime()) / DAY_MS) + 1;
  const severance = avgDailyWage * 30 * (serviceDays / 365);
  return { periodStart, periodDays, avgDailyWage, serviceDays, severance };
}

const fmtDateStr = (s: string) => {
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const fmt = (n: number) => Math.round(n).toLocaleString('ko-KR');

const formatHireDate = (e: Employee): string => {
  const ts = e.hire_date ?? e.hireDate;
  const d = ts?.toDate?.();
  if (!d) return '-';
  return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
};

const payRateLabel = (e: Employee): string => {
  const pt = resolvePayType(e.payType, e.isHourly);
  if (pt === 'hourly') return `시급 ${fmt(e.hourlyRate)}원`;
  if (pt === 'monthly') return `월급 ${fmt(e.monthlySalary)}원`;
  return `일당 ${fmt(e.dailyRate ?? 0)}원`;
};

/* ─────────────────────────── 인쇄용 공통 스타일 ─────────────────────────── */

const printTable: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
  color: 'var(--fg-strong)',
};
const th: React.CSSProperties = {
  border: '1px solid var(--c-cool-300)',
  background: 'var(--c-cool-100)',
  padding: '6px 8px',
  fontWeight: 700,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  border: '1px solid var(--c-cool-300)',
  padding: '6px 8px',
  textAlign: 'center',
};
const tdRight: React.CSSProperties = { ...td, textAlign: 'right' };

const PrintHeader: React.FC<{ title: string; businessName: string; subtitle: string }> = ({
  title,
  businessName,
  subtitle,
}) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--fg-strong)', textAlign: 'center' }}>
      {title}
    </div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 10,
        fontSize: 13,
        color: 'var(--c-cool-900)',
      }}
    >
      <span>사업장: {businessName}</span>
      <span>{subtitle}</span>
    </div>
  </div>
);

/* ─────────────────────────── 1) 근로자명부 ─────────────────────────── */

const RosterDoc: React.FC<{ employees: Employee[]; businessName: string; issuedAt: string }> = ({
  employees,
  businessName,
  issuedAt,
}) => {
  const sorted = [...employees].sort(
    (a, b) => Number(a.isRetired ?? false) - Number(b.isRetired ?? false),
  );
  return (
    <div>
      <PrintHeader title="근로자명부" businessName={businessName} subtitle={`작성일: ${issuedAt}`} />
      <table style={printTable}>
        <thead>
          <tr>
            <th style={th}>No.</th>
            <th style={th}>성명</th>
            <th style={th}>고용형태</th>
            <th style={th}>입사일</th>
            <th style={th}>임금</th>
            <th style={th}>주 근무시간</th>
            <th style={th}>4대보험</th>
            <th style={th}>재직 상태</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e, i) => (
            <tr key={e.id}>
              <td style={td}>{i + 1}</td>
              <td style={td}>{e.name}</td>
              <td style={td}>{payTypeChipLabel(e)}</td>
              <td style={td}>{formatHireDate(e)}</td>
              <td style={tdRight}>{payRateLabel(e)}</td>
              <td style={td}>{e.weeklyWorkHours ? `${e.weeklyWorkHours}시간` : '-'}</td>
              <td style={td}>{e.hasFourInsurance ? '가입' : '미가입'}</td>
              <td style={td}>{e.isRetired ? '퇴직' : '재직'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--c-cool-800)' }}>
        ※ 근로기준법 제42조에 따라 3년간 보존해야 하는 서류입니다.
      </div>
    </div>
  );
};

/* ─────────────────────────── 2) 출근부 ─────────────────────────── */

const AttendanceDoc: React.FC<{
  employees: Employee[];
  schedulesInMonth: WorkSchedule[];
  month: Date;
  businessName: string;
}> = ({ employees, schedulesInMonth, month, businessName }) => {
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  // 직원 × 일자 근무시간 매트릭스
  const rows = employees
    .map((e) => {
      const byDay: number[] = Array.from({ length: daysInMonth }, () => 0);
      schedulesInMonth
        .filter((s) => s.employee_id === e.id)
        .forEach((s) => {
          const d = new Date(s.date);
          if (isNaN(d.getTime())) return;
          byDay[d.getDate() - 1] += calculateTotalWorkHours([s]);
        });
      const total = byDay.reduce((a, b) => a + b, 0);
      return { employee: e, byDay, total };
    })
    .filter((r) => r.total > 0 || !r.employee.isRetired);

  const cellSmall: React.CSSProperties = { ...td, padding: '4px 2px', fontSize: 10, minWidth: 24 };

  return (
    <div>
      <PrintHeader
        title="출근부 (근로시간 기록부)"
        businessName={businessName}
        subtitle={`${month.getFullYear()}년 ${month.getMonth() + 1}월`}
      />
      <table style={printTable}>
        <thead>
          <tr>
            <th style={{ ...th, minWidth: 64 }}>성명</th>
            {Array.from({ length: daysInMonth }, (_, i) => {
              const dow = new Date(month.getFullYear(), month.getMonth(), i + 1).getDay();
              return (
                <th key={i} style={{ ...th, padding: '4px 2px', fontSize: 10 }}>
                  {i + 1}
                  <br />
                  <span style={{ fontWeight: 400, color: dow === 0 ? 'var(--c-red-600)' : dow === 6 ? 'var(--c-cool-800)' : undefined }}>
                    {dayNames[dow]}
                  </span>
                </th>
              );
            })}
            <th style={th}>합계</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ employee, byDay, total }) => (
            <tr key={employee.id}>
              <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{employee.name}</td>
              {byDay.map((h, i) => (
                <td key={i} style={cellSmall}>
                  {h > 0 ? Math.round(h * 10) / 10 : ''}
                </td>
              ))}
              <td style={{ ...tdRight, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {Math.round(total * 10) / 10}시간
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--c-cool-800)' }}>
        ※ 숫자는 해당 일의 근무시간(휴게시간 제외)입니다. 주휴·연장수당 산정 근거 자료로 3년 보존을
        권장합니다.
      </div>
    </div>
  );
};

/* ─────────────────────────── 3) 임금대장 ─────────────────────────── */

const LedgerDoc: React.FC<{
  payroll: MonthlyPayrollData[];
  month: Date;
  businessName: string;
}> = ({ payroll, month, businessName }) => {
  const rows = payroll.filter((p) => p.totalSalary > 0 || !p.employee.isRetired);
  const sum = (f: (p: MonthlyPayrollData) => number) => rows.reduce((a, p) => a + f(p), 0);
  const insuranceOf = (p: MonthlyPayrollData) => p.insurance?.total ?? 0;
  const taxOf = (p: MonthlyPayrollData) => (p.tax?.withholding ?? 0) + (p.tax?.local ?? 0);

  return (
    <div>
      <PrintHeader
        title="임금대장"
        businessName={businessName}
        subtitle={`귀속: ${month.getFullYear()}년 ${month.getMonth() + 1}월`}
      />
      <table style={printTable}>
        <thead>
          <tr>
            <th style={th}>성명</th>
            <th style={th}>고용형태</th>
            <th style={th}>근무시간</th>
            <th style={th}>기본급</th>
            <th style={th}>주휴수당</th>
            <th style={th}>기타수당</th>
            <th style={th}>지급액 계</th>
            <th style={th}>4대보험</th>
            <th style={th}>소득세+지방세</th>
            <th style={th}>실수령액</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const extras =
              (p.overtimePay ?? 0) + (p.nightPay ?? 0) + (p.holidayPay ?? 0) + (p.laborDayAllowance ?? 0);
            return (
              <tr key={p.employee.id}>
                <td style={td}>{p.employee.name}</td>
                <td style={td}>{payTypeChipLabel(p.employee)}</td>
                <td style={td}>
                  {p.workDays != null ? `${p.workDays}일` : `${Math.round(p.totalWorkHours)}시간`}
                </td>
                <td style={tdRight}>{fmt(p.baseSalary)}</td>
                <td style={tdRight}>{fmt(p.holidayAllowance)}</td>
                <td style={tdRight}>{fmt(extras)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(p.totalSalary)}</td>
                <td style={tdRight}>{fmt(insuranceOf(p))}</td>
                <td style={tdRight}>{fmt(taxOf(p))}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(p.netSalary)}</td>
              </tr>
            );
          })}
          <tr>
            <td style={{ ...th }} colSpan={3}>
              합계
            </td>
            <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(sum((p) => p.baseSalary))}</td>
            <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(sum((p) => p.holidayAllowance))}</td>
            <td style={{ ...tdRight, fontWeight: 700 }}>
              {fmt(
                sum(
                  (p) =>
                    (p.overtimePay ?? 0) +
                    (p.nightPay ?? 0) +
                    (p.holidayPay ?? 0) +
                    (p.laborDayAllowance ?? 0),
                ),
              )}
            </td>
            <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(sum((p) => p.totalSalary))}</td>
            <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(sum(insuranceOf))}</td>
            <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(sum(taxOf))}</td>
            <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(sum((p) => p.netSalary))}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--c-cool-800)' }}>
        ※ 근로기준법 제48조에 따른 임금대장입니다. 3년간 보존해야 합니다.
      </div>
    </div>
  );
};

/* ─────────────────────────── 4) 임금명세서 ─────────────────────────── */

const PayStubDoc: React.FC<{
  data: MonthlyPayrollData;
  month: Date;
  businessName: string;
  /** 1인 1페이지 정렬용 고정 높이 (전체 출력 시) */
  fixedHeight?: boolean;
}> = ({ data, month, businessName, fixedHeight = false }) => {
  const e = data.employee;
  const allPayItems: Array<[string, number]> = [
    ['기본급', data.baseSalary],
    ['주휴수당', data.holidayAllowance],
    ['연장근로수당', data.overtimePay ?? 0],
    ['야간근로수당', data.nightPay ?? 0],
    ['휴일근로수당', data.holidayPay ?? 0],
    ['근로자의 날 수당', data.laborDayAllowance ?? 0],
  ];
  const payItems = allPayItems.filter(([, v]) => v > 0);

  const allDeductions: Array<[string, number]> = [
    ['국민연금', data.insurance?.pension ?? 0],
    ['건강보험', data.insurance?.health ?? 0],
    ['장기요양보험', data.insurance?.longTermCare ?? 0],
    ['고용보험', data.insurance?.employment ?? 0],
    ['소득세', data.tax?.withholding ?? 0],
    ['지방소득세', data.tax?.local ?? 0],
  ];
  const deductions = allDeductions.filter(([, v]) => v > 0);

  const deductionTotal = deductions.reduce((a, [, v]) => a + v, 0);
  const pt = resolvePayType(e.payType, e.isHourly);

  return (
    <div
      style={{
        height: fixedHeight ? A4_PORTRAIT_HEIGHT : undefined,
        boxSizing: 'border-box',
        padding: fixedHeight ? '48px 0 0' : undefined,
      }}
    >
      <PrintHeader
        title="임금명세서"
        businessName={businessName}
        subtitle={`귀속: ${month.getFullYear()}년 ${month.getMonth() + 1}월 · 지급일: 매월 ${e.payday || '-'}일`}
      />
      <table style={{ ...printTable, marginBottom: 14 }}>
        <tbody>
          <tr>
            <th style={{ ...th, width: '18%' }}>성명</th>
            <td style={td}>{e.name}</td>
            <th style={{ ...th, width: '18%' }}>고용형태</th>
            <td style={td}>{payTypeChipLabel(e)}</td>
          </tr>
          <tr>
            <th style={th}>입사일</th>
            <td style={td}>{formatHireDate(e)}</td>
            <th style={th}>임금 기준</th>
            <td style={td}>{payRateLabel(e)}</td>
          </tr>
          <tr>
            <th style={th}>근무</th>
            <td style={td} colSpan={3}>
              {data.workDays != null
                ? `${data.workDays}일`
                : `${Math.round(data.totalWorkHours * 10) / 10}시간`}
              {pt === 'hourly' &&
                ` (계산방법: 근무시간 × 시급 ${fmt(e.hourlyRate)}원${e.isweeklyWork ? ' + 주휴수당' : ''})`}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 14 }}>
        {/* 지급 항목 */}
        <table style={{ ...printTable, flex: 1 }}>
          <thead>
            <tr>
              <th style={th} colSpan={2}>
                지급 항목
              </th>
            </tr>
          </thead>
          <tbody>
            {payItems.map(([label, v]) => (
              <tr key={label}>
                <td style={td}>{label}</td>
                <td style={tdRight}>{fmt(v)}원</td>
              </tr>
            ))}
            <tr>
              <th style={th}>지급액 계</th>
              <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(data.totalSalary)}원</td>
            </tr>
          </tbody>
        </table>

        {/* 공제 항목 */}
        <table style={{ ...printTable, flex: 1 }}>
          <thead>
            <tr>
              <th style={th} colSpan={2}>
                공제 항목
              </th>
            </tr>
          </thead>
          <tbody>
            {deductions.length === 0 && (
              <tr>
                <td style={td} colSpan={2}>
                  공제 내역 없음
                </td>
              </tr>
            )}
            {deductions.map(([label, v]) => (
              <tr key={label}>
                <td style={td}>{label}</td>
                <td style={tdRight}>{fmt(v)}원</td>
              </tr>
            ))}
            <tr>
              <th style={th}>공제액 계</th>
              <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(deductionTotal)}원</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: 14,
          padding: '12px 16px',
          border: '2px solid var(--fg-strong)',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 16,
          fontWeight: 800,
          color: 'var(--fg-strong)',
        }}
      >
        <span>실수령액</span>
        <span>{fmt(data.netSalary)}원</span>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--c-cool-800)' }}>
        ※ 근로기준법 제48조 제2항에 따라 임금 지급 시 교부해야 하는 임금명세서입니다.
      </div>
    </div>
  );
};

/* ─────────────────────────── 5) 해고 예고 통지서 ─────────────────────────── */

const DismissalNoticeDoc: React.FC<{
  employee: Employee;
  form: DismissalForm;
  businessName: string;
  issuedAt: string;
}> = ({ employee, form, businessName, issuedAt }) => (
  <div>
    <PrintHeader title="해고 예고 통지서" businessName={businessName} subtitle={`통지일: ${issuedAt}`} />
    <table style={{ ...printTable, marginBottom: 18 }}>
      <tbody>
        <tr>
          <th style={{ ...th, width: '22%' }}>대상 직원</th>
          <td style={td}>{employee.name}</td>
        </tr>
        <tr>
          <th style={th}>입사일</th>
          <td style={td}>{formatHireDate(employee)}</td>
        </tr>
        <tr>
          <th style={th}>해고 예정일</th>
          <td style={{ ...td, fontWeight: 700 }}>{fmtDateStr(form.dismissDate)}</td>
        </tr>
        <tr>
          <th style={th}>해고 사유</th>
          <td style={{ ...td, textAlign: 'left', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {form.reason}
          </td>
        </tr>
      </tbody>
    </table>
    <div style={{ fontSize: 13, color: 'var(--fg-strong)', lineHeight: 1.9 }}>
      위와 같이 근로기준법 제26조에 따라 해고 예정일 30일 전에 해고를 예고하며, 같은 법
      제27조에 따라 해고 사유와 해고 시기를 서면으로 통지합니다.
    </div>
    <div style={{ marginTop: 40, textAlign: 'right', fontSize: 13, color: 'var(--fg-strong)' }}>
      <div style={{ marginBottom: 8 }}>{issuedAt}</div>
      <div>
        사업장: {businessName} &nbsp;&nbsp; 사용자: ____________ (서명 또는 인)
      </div>
    </div>
    <div style={{ marginTop: 28, fontSize: 11, color: 'var(--c-cool-800)', lineHeight: 1.7 }}>
      ※ 해고 예고 기간이 30일 미만이면 30일분 이상의 통상임금(해고예고수당)을 지급해야 합니다.
      <br />※ 본 통지서는 3년간 보존하세요. 부당해고 분쟁 시 핵심 증빙이 됩니다.
    </div>
  </div>
);

/* ─────────────────────────── 6) 퇴직금 산정서 ─────────────────────────── */

const SeveranceCalcDoc: React.FC<{
  employee: Employee;
  form: SeveranceForm;
  businessName: string;
  issuedAt: string;
}> = ({ employee, form, businessName, issuedAt }) => {
  const hireTs = employee.hire_date ?? employee.hireDate;
  const hire = hireTs?.toDate?.() ?? new Date();
  const retire = new Date(form.retireDate);
  const threeMonthPay = Number(form.threeMonthPay) || 0;
  const { periodStart, periodDays, avgDailyWage, serviceDays, severance } = calcSeverance(
    hire,
    retire,
    threeMonthPay,
  );
  const years = Math.floor(serviceDays / 365);
  const remDays = serviceDays % 365;

  return (
    <div>
      <PrintHeader title="퇴직금 산정서" businessName={businessName} subtitle={`작성일: ${issuedAt}`} />
      <table style={{ ...printTable, marginBottom: 16 }}>
        <tbody>
          <tr>
            <th style={{ ...th, width: '22%' }}>성명</th>
            <td style={td}>{employee.name}</td>
            <th style={{ ...th, width: '22%' }}>고용형태</th>
            <td style={td}>{payTypeChipLabel(employee)}</td>
          </tr>
          <tr>
            <th style={th}>입사일</th>
            <td style={td}>{formatHireDate(employee)}</td>
            <th style={th}>퇴직일</th>
            <td style={td}>{fmtDateStr(form.retireDate)}</td>
          </tr>
          <tr>
            <th style={th}>재직기간</th>
            <td style={td} colSpan={3}>
              {years}년 {remDays}일 (총 {serviceDays.toLocaleString('ko-KR')}일)
            </td>
          </tr>
        </tbody>
      </table>

      <table style={printTable}>
        <thead>
          <tr>
            <th style={th} colSpan={2}>산정 내역</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={td}>① 평균임금 산정기간 (퇴직 전 3개월)</td>
            <td style={tdRight}>
              {`${periodStart.getFullYear()}.${periodStart.getMonth() + 1}.${periodStart.getDate()}`} ~{' '}
              {`${retire.getFullYear()}.${retire.getMonth() + 1}.${retire.getDate()}`} ({periodDays}일)
            </td>
          </tr>
          <tr>
            <td style={td}>② 산정기간 임금총액</td>
            <td style={tdRight}>{fmt(threeMonthPay)}원</td>
          </tr>
          <tr>
            <td style={td}>③ 1일 평균임금 (② ÷ {periodDays}일)</td>
            <td style={tdRight}>{fmt(avgDailyWage)}원</td>
          </tr>
          <tr>
            <td style={td}>④ 퇴직금 = ③ × 30일 × (재직일수 ÷ 365)</td>
            <td style={{ ...tdRight, fontWeight: 800, fontSize: 14 }}>{fmt(severance)}원</td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 14, fontSize: 11, color: 'var(--c-cool-800)', lineHeight: 1.8 }}>
        ※ 퇴직금은 퇴직일로부터 14일 이내에 지급해야 합니다 (근로자퇴직급여 보장법 제9조).
        <br />※ 본 산정액은 입력된 3개월 임금총액 기준 추정치입니다. 상여금·연차수당 포함 여부,
        평균임금과 통상임금 비교 등에 따라 달라질 수 있으니 지급 전 노무사 확인을 권장합니다.
        <br />※ 계속근로기간 1년 미만이거나 4주 평균 주 15시간 미만 근로자는 지급 대상이 아닙니다.
      </div>
    </div>
  );
};

/* ─────────────────────────── 탭 본체 ─────────────────────────── */

interface HrRecordsTabProps {
  selectedMonth: Date;
  onMonthChange: (month: Date) => void;
}

const HrRecordsTab: React.FC<HrRecordsTabProps> = ({ selectedMonth, onMonthChange }) => {
  const { employees, loading: employeesLoading } = useEmployeeData();
  const { schedules } = useWorkScheduleData(selectedMonth);
  const { monthlyPayrollData, schedulesInMonth } = useEmployeePayroll(
    employees,
    schedules,
    selectedMonth,
  );
  const currentAccountBook = useAccountBookStore((s) => s.currentAccountBook);
  const businessName =
    currentAccountBook?.businessName || currentAccountBook?.name || '사업장';

  const activeEmployees = useMemo(() => employees.filter((e) => !e.isRetired), [employees]);
  const [stubEmployeeId, setStubEmployeeId] = useState<string>('ALL');
  const [printJob, setPrintJob] = useState<PrintJob | null>(null);
  const printRef = useRef<HTMLDivElement | null>(null);

  // 해고 예고 통지서 / 퇴직금 산정서 — 입력 다이얼로그
  const [dismissalOpen, setDismissalOpen] = useState(false);
  const [dismissalForm, setDismissalForm] = useState<DismissalForm>({
    employeeId: '',
    dismissDate: '',
    reason: '',
  });
  const [severanceOpen, setSeveranceOpen] = useState(false);
  const [severanceForm, setSeveranceForm] = useState<SeveranceForm>({
    employeeId: '',
    retireDate: '',
    threeMonthPay: '',
  });

  const dismissalEmployee = useMemo(
    () => employees.find((e) => e.id === dismissalForm.employeeId) ?? null,
    [employees, dismissalForm.employeeId],
  );
  const severanceEmployee = useMemo(
    () => employees.find((e) => e.id === severanceForm.employeeId) ?? null,
    [employees, severanceForm.employeeId],
  );

  // 해고 예고 30일 검증 — 미달이면 해고예고수당 안내
  const dismissalNoticeDays = useMemo(() => {
    if (!dismissalForm.dismissDate) return null;
    const target = new Date(dismissalForm.dismissDate);
    if (isNaN(target.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((target.getTime() - today.getTime()) / DAY_MS);
  }, [dismissalForm.dismissDate]);

  const monthLabel = `${selectedMonth.getFullYear()}년 ${selectedMonth.getMonth() + 1}월`;
  const issuedAt = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
  }, []);

  const changeMonth = (delta: number) => {
    onMonthChange(
      new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + delta, 1),
    );
  };

  // 인쇄 대상이 렌더되면 캡처 → PDF 저장 → 정리
  useEffect(() => {
    if (!printJob || !printRef.current) return;
    const orientation = printJob.type === 'attendance' ? 'landscape' : 'portrait';
    // 렌더 완료 다음 프레임에 캡처
    const id = window.setTimeout(async () => {
      try {
        await downloadElementAsPdf(printRef.current!, printJob.fileName, orientation);
      } catch (err) {
        console.error('PDF 생성 실패:', err);
        alert('PDF 생성에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setPrintJob(null);
      }
    }, 80);
    return () => window.clearTimeout(id);
  }, [printJob]);

  const stubTargets = useMemo(() => {
    const paid = monthlyPayrollData.filter((p) => p.totalSalary > 0);
    if (!printJob || printJob.type !== 'paystub') return [];
    if (printJob.employeeId === 'ALL') return paid;
    return monthlyPayrollData.filter((p) => p.employee.id === printJob.employeeId);
  }, [printJob, monthlyPayrollData]);

  const docCards: Array<{
    type: DocType;
    title: string;
    legal: string;
    description: string;
    disabled: boolean;
    extra?: React.ReactNode;
    /** PDF 즉시 저장 대신 입력 다이얼로그를 여는 카드 */
    onClick?: () => void;
    buttonLabel?: string;
  }> = [
    {
      type: 'roster',
      title: '근로자명부',
      legal: '법정 의무 · 3년 보존',
      description: '등록된 직원 정보로 명부를 만듭니다. 퇴직자는 상태로 구분 표시됩니다.',
      disabled: employees.length === 0,
    },
    {
      type: 'attendance',
      title: '출근부',
      legal: '권장 · 3년 보존',
      description: `${monthLabel} 근무 일정을 일자별 근무시간 표로 만듭니다 (가로 A4).`,
      disabled: schedulesInMonth.length === 0,
    },
    {
      type: 'ledger',
      title: '임금대장',
      legal: '법정 의무 · 3년 보존',
      description: `${monthLabel} 급여 계산 결과를 직원별 대장으로 만듭니다.`,
      disabled: monthlyPayrollData.length === 0,
    },
    {
      type: 'paystub',
      title: '임금명세서',
      legal: '법정 의무 · 교부 필수',
      description: '직원별 지급·공제 내역 명세서. 전체 선택 시 1인 1페이지로 출력됩니다.',
      disabled: monthlyPayrollData.filter((p) => p.totalSalary > 0).length === 0,
      extra: (
        <Select
          size="small"
          value={stubEmployeeId}
          onChange={(e) => setStubEmployeeId(e.target.value)}
          sx={{ minWidth: 140, mr: 1 }}
        >
          <MenuItem value="ALL">전체 직원</MenuItem>
          {activeEmployees.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      type: 'dismissal',
      title: '해고 예고 통지서',
      legal: '법정 의무 · 30일 전 예고',
      description: '직원·해고 예정일·사유를 입력하면 근로기준법 제26·27조에 맞는 서면 통지서를 만듭니다.',
      disabled: activeEmployees.length === 0,
      onClick: () => setDismissalOpen(true),
      buttonLabel: '작성하기',
    },
    {
      type: 'severance',
      title: '퇴직금 산정서',
      legal: '법정 의무 · 14일 내 지급',
      description: '입사일·퇴직일·3개월 임금총액으로 법정 산식(평균임금×30일×재직년수) 산정 내역서를 만듭니다.',
      disabled: employees.length === 0,
      onClick: () => setSeveranceOpen(true),
      buttonLabel: '작성하기',
    },
  ];

  if (employeesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
        <CircularProgress sx={{ color: 'var(--primary)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* 헤더 + 월 선택 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.031em',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            법정 인사 서류
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: '-0.019em',
              color: 'var(--fg-strong)',
              m: 0,
            }}
          >
            인사기록 PDF 만들기
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 15, color: 'var(--fg-tertiary)' }}>
            등록된 직원·근무·급여 데이터로 바로 생성합니다 — 별도 입력 없음
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'var(--bg-elev-2)',
            borderRadius: 'var(--r-pill)',
            px: 1.5,
            py: 0.5,
            border: '1px solid var(--border-subtle)',
          }}
        >
          <IconButton size="small" onClick={() => changeMonth(-1)} aria-label="이전"
            sx={{ fontSize: 22, lineHeight: 1, color: 'var(--primary)' }}>
            {GLYPHS.prev}
          </IconButton>
          <Typography
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              color: 'var(--fg-strong)',
              minWidth: 110,
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {monthLabel}
          </Typography>
          <IconButton size="small" onClick={() => changeMonth(1)} aria-label="다음"
            sx={{ fontSize: 22, lineHeight: 1, color: 'var(--primary)' }}>
            {GLYPHS.next}
          </IconButton>
        </Box>
      </Box>

      {/* 문서 카드 */}
      <Grid container spacing={2}>
        {docCards.map((card) => (
          <Grid item xs={12} md={6} key={card.type}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: 'var(--fg-strong)' }}>
                    {card.title}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      ml: 'auto',
                      borderRadius: 'var(--r-pill)',
                      px: 1.25,
                      py: 0.25,
                      fontSize: 12,
                      fontWeight: 700,
                      backgroundColor: 'var(--primary-soft)',
                      color: 'var(--c-green-800)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {card.legal}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 15, color: 'var(--fg-secondary)', lineHeight: 1.6 }}>
                  {card.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto', pt: 1 }}>
                  {card.extra}
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={
                      printJob?.type === card.type ? (
                        <CircularProgress size={16} sx={{ color: 'var(--fg-on-primary)' }} />
                      ) : undefined
                    }
                    disabled={card.disabled || printJob !== null}
                    onClick={() => {
                      if (card.onClick) {
                        card.onClick();
                        return;
                      }
                      const fileNames: Partial<Record<DocType, string>> = {
                        roster: `근로자명부_${businessName}_${issuedAt}.pdf`,
                        attendance: `출근부_${businessName}_${monthLabel}.pdf`,
                        ledger: `임금대장_${businessName}_${monthLabel}.pdf`,
                        paystub: `임금명세서_${businessName}_${monthLabel}.pdf`,
                      };
                      setPrintJob({
                        type: card.type,
                        employeeId: card.type === 'paystub' ? stubEmployeeId : undefined,
                        fileName: fileNames[card.type] ?? `${card.title}.pdf`,
                      });
                    }}
                    sx={{
                      backgroundColor: 'var(--primary)',
                      '&:hover': { backgroundColor: 'var(--primary-hover)' },
                    }}
                  >
                    {card.buttonLabel ?? 'PDF 저장'}
                  </Button>
                  {card.disabled && (
                    <Typography sx={{ ml: 1.5, fontSize: 13, color: 'var(--fg-tertiary)' }}>
                      생성할 데이터가 없습니다
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 인쇄용 오프스크린 렌더 영역 — 캡처 후 제거 */}
      {printJob && (
        <Box
          ref={printRef}
          sx={{
            position: 'fixed',
            left: -10000,
            top: 0,
            width: printJob.type === 'attendance' ? A4_LANDSCAPE_WIDTH : A4_PORTRAIT_WIDTH,
            backgroundColor: 'var(--bg-base)',
            p: printJob.type === 'paystub' ? 0 : 6,
            zIndex: -1,
          }}
        >
          {printJob.type === 'roster' && (
            <RosterDoc employees={employees} businessName={businessName} issuedAt={issuedAt} />
          )}
          {printJob.type === 'attendance' && (
            <AttendanceDoc
              employees={employees}
              schedulesInMonth={schedulesInMonth}
              month={selectedMonth}
              businessName={businessName}
            />
          )}
          {printJob.type === 'ledger' && (
            <LedgerDoc payroll={monthlyPayrollData} month={selectedMonth} businessName={businessName} />
          )}
          {printJob.type === 'paystub' &&
            stubTargets.map((p) => (
              <Box key={p.employee.id} sx={{ px: 6 }}>
                <PayStubDoc
                  data={p}
                  month={selectedMonth}
                  businessName={businessName}
                  fixedHeight={stubTargets.length > 1}
                />
              </Box>
            ))}
          {printJob.type === 'dismissal' && dismissalEmployee && (
            <DismissalNoticeDoc
              employee={dismissalEmployee}
              form={dismissalForm}
              businessName={businessName}
              issuedAt={issuedAt}
            />
          )}
          {printJob.type === 'severance' && severanceEmployee && (
            <SeveranceCalcDoc
              employee={severanceEmployee}
              form={severanceForm}
              businessName={businessName}
              issuedAt={issuedAt}
            />
          )}
        </Box>
      )}

      {/* 해고 예고 통지서 — 입력 다이얼로그 */}
      <AppDialog
        open={dismissalOpen}
        title="해고 예고 통지서 작성"
        onClose={() => setDismissalOpen(false)}
        maxWidth="sm"
        actions={
          <>
            <Button variant="text" onClick={() => setDismissalOpen(false)}>취소</Button>
            <Button
              variant="contained"
              disableElevation
              disabled={
                !dismissalEmployee || !dismissalForm.dismissDate || !dismissalForm.reason.trim()
              }
              onClick={() => {
                setDismissalOpen(false);
                setPrintJob({
                  type: 'dismissal',
                  fileName: `해고예고통지서_${dismissalEmployee?.name}_${issuedAt}.pdf`,
                });
              }}
              sx={{
                backgroundColor: 'var(--primary)',
                '&:hover': { backgroundColor: 'var(--primary-hover)' },
              }}
            >
              PDF 저장
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="dismissal-employee-label">대상 직원</InputLabel>
            <Select
              labelId="dismissal-employee-label"
              label="대상 직원"
              value={dismissalForm.employeeId}
              onChange={(e) => setDismissalForm((f) => ({ ...f, employeeId: e.target.value }))}
            >
              {activeEmployees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name} ({payTypeChipLabel(e)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="해고 예정일"
            InputLabelProps={{ shrink: true }}
            value={dismissalForm.dismissDate}
            onChange={(e) => setDismissalForm((f) => ({ ...f, dismissDate: e.target.value }))}
          />
          {dismissalNoticeDays != null && dismissalNoticeDays < 30 && (
            <Typography sx={{ fontSize: 14, color: 'var(--c-orange-600)', fontWeight: 600, lineHeight: 1.6 }}>
              예고일까지 {dismissalNoticeDays}일 — 30일 미만이면 30일분 이상의 통상임금(해고예고수당)을
              지급해야 합니다.
            </Typography>
          )}
          <TextField
            size="small"
            label="해고 사유 (구체적으로 기재)"
            multiline
            minRows={3}
            value={dismissalForm.reason}
            onChange={(e) => setDismissalForm((f) => ({ ...f, reason: e.target.value }))}
            helperText="사유가 추상적이면 부당해고 분쟁에서 불리할 수 있습니다."
          />
        </Box>
      </AppDialog>

      {/* 퇴직금 산정서 — 입력 다이얼로그 */}
      <AppDialog
        open={severanceOpen}
        title="퇴직금 산정서 작성"
        onClose={() => setSeveranceOpen(false)}
        maxWidth="sm"
        actions={
          <>
            <Button variant="text" onClick={() => setSeveranceOpen(false)}>취소</Button>
            <Button
              variant="contained"
              disableElevation
              disabled={
                !severanceEmployee ||
                !severanceForm.retireDate ||
                !(Number(severanceForm.threeMonthPay) > 0) ||
                !(severanceEmployee.hire_date ?? severanceEmployee.hireDate)
              }
              onClick={() => {
                setSeveranceOpen(false);
                setPrintJob({
                  type: 'severance',
                  fileName: `퇴직금산정서_${severanceEmployee?.name}_${issuedAt}.pdf`,
                });
              }}
              sx={{
                backgroundColor: 'var(--primary)',
                '&:hover': { backgroundColor: 'var(--primary-hover)' },
              }}
            >
              PDF 저장
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="severance-employee-label">대상 직원</InputLabel>
            <Select
              labelId="severance-employee-label"
              label="대상 직원"
              value={severanceForm.employeeId}
              onChange={(e) => {
                const id = e.target.value;
                // 이번 달 급여 × 3을 제안값으로 미리 채움 (수정 가능)
                const pay = monthlyPayrollData.find((p) => p.employee.id === id);
                setSeveranceForm((f) => ({
                  ...f,
                  employeeId: id,
                  threeMonthPay:
                    f.threeMonthPay || (pay && pay.totalSalary > 0 ? String(pay.totalSalary * 3) : ''),
                }));
              }}
            >
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name} ({payTypeChipLabel(e)}{e.isRetired ? ' · 퇴직' : ''}) — 입사 {formatHireDate(e)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="퇴직일"
            InputLabelProps={{ shrink: true }}
            value={severanceForm.retireDate}
            onChange={(e) => setSeveranceForm((f) => ({ ...f, retireDate: e.target.value }))}
          />
          <TextField
            size="small"
            type="number"
            label="퇴직 전 3개월 임금총액 (원)"
            value={severanceForm.threeMonthPay}
            onChange={(e) => setSeveranceForm((f) => ({ ...f, threeMonthPay: e.target.value }))}
            helperText="이번 달 급여 × 3이 제안값으로 채워집니다. 상여금·수당 포함 실제 금액으로 수정하세요."
          />
          {severanceEmployee && severanceForm.retireDate && Number(severanceForm.threeMonthPay) > 0 && (() => {
            const hireTs = severanceEmployee.hire_date ?? severanceEmployee.hireDate;
            const hire = hireTs?.toDate?.();
            if (!hire) {
              return (
                <Typography sx={{ fontSize: 14, color: 'var(--c-orange-600)', fontWeight: 600 }}>
                  입사일이 등록되지 않은 직원입니다. 직원관리에서 입사일을 먼저 입력하세요.
                </Typography>
              );
            }
            const { severance, serviceDays } = calcSeverance(
              hire,
              new Date(severanceForm.retireDate),
              Number(severanceForm.threeMonthPay),
            );
            return (
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: 'var(--primary-bg)',
                  border: '1px solid var(--primary-soft)',
                  borderRadius: 'var(--r-12)',
                }}
              >
                <Typography sx={{ fontSize: 14, color: 'var(--fg-secondary)' }}>
                  예상 퇴직금{serviceDays < 365 ? ' (1년 미만 — 지급 대상 아님)' : ''}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: serviceDays < 365 ? 'var(--fg-tertiary)' : 'var(--primary-press)',
                    fontFamily: 'var(--font-numeric)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmt(severance)}원
                </Typography>
              </Box>
            );
          })()}
        </Box>
      </AppDialog>
    </Box>
  );
};

export default HrRecordsTab;
